import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type {
  ShareableDatumEntity,
  LegacyWallDatumEntity,
  LegacyFoundationDatumEntity,
  LegacyLevelDatumEntity,
  DatumEntity,
  StackedLevelDatumEntity,
  WallPlaneHorizontalDatumEntity,
} from "building-types/datums/datumEntityTypes";
import { DatumDirection, DatumType } from "building-types/datums/datumEntityTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import {
  AbsoluteVerticalDatumAbstraction,
  LegacyWallVerticalDatumAbstraction,
  LegacyWallHorizontalDatumAbstraction,
  LegacyFoundationVerticalDatumAbstraction,
  LegacyFoundationLateralDatumAbstraction,
  LegacyLevelDatumAbstraction,
  StackedLevelDatumAbstraction,
  WallPlaneHorizontalDatumAbstraction,
} from "./datumAbstractions";

/**
 * Factory functions for creating a DatumAbstraction from a DatumEntity
 */
function createShareableDatumAbstraction(datum: ShareableDatumEntity): DatumAbstraction {
  const { direction } = datum;
  switch (direction) {
    case DatumDirection.VERTICAL: {
      return new AbsoluteVerticalDatumAbstraction(datum);
    }
    default: {
      assertUnreachable(direction as never);
    }
  }
}

function createWallDatumAbstraction(datum: LegacyWallDatumEntity): DatumAbstraction {
  const { direction } = datum;
  switch (direction) {
    case DatumDirection.VERTICAL: {
      return new LegacyWallVerticalDatumAbstraction(datum);
    }
    case DatumDirection.HORIZONTAL: {
      return new LegacyWallHorizontalDatumAbstraction(datum);
    }
    default: {
      assertUnreachable(direction as never);
    }
  }
}

function createFoundationDatumAbstraction(datum: LegacyFoundationDatumEntity): DatumAbstraction {
  const { direction } = datum;
  switch (direction) {
    case DatumDirection.VERTICAL: {
      return new LegacyFoundationVerticalDatumAbstraction(datum);
    }
    case DatumDirection.LATERAL: {
      return new LegacyFoundationLateralDatumAbstraction(datum);
    }
    default: {
      assertUnreachable(direction as never);
    }
  }
}

function createLevelDatumAbstraction(datum: LegacyLevelDatumEntity): DatumAbstraction {
  const { direction } = datum;
  switch (direction) {
    case DatumDirection.VERTICAL: {
      return new LegacyLevelDatumAbstraction(datum);
    }
    default: {
      assertUnreachable(direction as never);
    }
  }
}

function createWallPlaneDatumAbstraction(datum: WallPlaneHorizontalDatumEntity): DatumAbstraction {
  const { direction } = datum;
  switch (direction) {
    case DatumDirection.HORIZONTAL: {
      return new WallPlaneHorizontalDatumAbstraction(datum);
    }
    default: {
      assertUnreachable(direction as never);
    }
  }
}

function createStackedLevelDatumAbstraction(datum: StackedLevelDatumEntity): DatumAbstraction {
  const { direction } = datum;
  switch (direction) {
    case DatumDirection.VERTICAL: {
      return new StackedLevelDatumAbstraction(datum);
    }
    default: {
      assertUnreachable(direction as never);
    }
  }
}
export function createDatumAbstraction(datum: DatumEntity): DatumAbstraction {
  const { datumType } = datum;
  switch (datumType) {
    case DatumType.SHAREABLE: {
      return createShareableDatumAbstraction(datum);
    }
    case DatumType.WALL: {
      return createWallDatumAbstraction(datum);
    }
    case DatumType.FOUNDATION: {
      return createFoundationDatumAbstraction(datum);
    }
    case DatumType.STACKED_LEVEL: {
      return createStackedLevelDatumAbstraction(datum);
    }
    case DatumType.LEVEL: {
      return createLevelDatumAbstraction(datum);
    }
    case DatumType.WALL_PLANE: {
      return createWallPlaneDatumAbstraction(datum);
    }
    default: {
      assertUnreachable(datumType as never);
    }
  }
}
